import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Coins, Search } from 'lucide-react';

import { MasterLookupBar }    from '@/components/master/MasterLookupBar';
import { MasterPageHeader }   from '@/components/master/MasterPageHeader';
import { MasterFlash }        from '@/components/master/MasterFlash';
import { MasterAuditTab }     from '@/components/master/MasterAuditTab';
import { MasterToolbar }       from '@/components/master/MasterToolbar';
import { MasterSearchModal }   from '@/components/master/MasterSearchModal';
import type { SearchColumn }   from '@/components/master/MasterSearchModal';
import { ConfirmDialog }       from '@/components/ui/ConfirmDialog';
import { Input }               from '@/components/ui/Input';
import { Badge }               from '@/components/ui/Badge';
import { Toggle } from '@/components/ui/Toggle';
import { Button } from '@/components/ui/Button';
import { Tabs } from '@/components/ui/Tabs';
import { Field } from '@/components/ui/Field';
import { Card }                from '@/components/ui/Card';

import { usePermission }        from '@/hooks/usePermission';
import { useFlashMessage }    from '@/hooks/useFlashMessage';
import { useMasterScreenState } from '@/hooks/useMasterScreenState';
import { Permissions }          from '@/constants/permissions';
import { AppRoutes }            from '@/constants/routes';
import { cashDenominationApi }  from '@/api/cashDenomination.api';
import type { CashDenominationDto, CashDenominationListDto } from '@/types/cashDenomination.types';
import type { RootState } from '@/state/store';
import { cn } from '@/utils/cn';

// ── Zod schema ─────────────────────────────────────────────────────────────────

const schema = z.object({
  currencyCode:      z.string().min(1, 'Currency code is required').max(10),
  denominationValue: z.coerce.number().positive('Value must be positive'),
  denominationType:  z.enum(['Note', 'Coin']),
  countryCode:       z.string().max(10).nullable().optional(),
  displayOrder:      z.coerce.number().int().min(0),
  isActive:          z.boolean(),
});

type FormValues = z.infer<typeof schema>;

const EMPTY_FORM: FormValues = {
  currencyCode: '', denominationValue: 0, denominationType: 'Note',
  countryCode: null, displayOrder: 0, isActive: true,
};

const TABS = ['General', 'Audit'] as const;
type Tab = (typeof TABS)[number];

const SEARCH_COLUMNS: SearchColumn<CashDenominationListDto>[] = [
  { key: 'currencyCode',      label: 'Currency',  className: 'w-24 font-mono' },
  { key: 'denominationValue', label: 'Value',     className: 'w-24',
    render: r => r.denominationValue.toFixed(2) },
  { key: 'denominationType',  label: 'Type',      className: 'w-20',
    render: r => <Badge variant={r.denominationType === 'Note' ? 'info' : 'default'}>{r.denominationType}</Badge> },
  { key: 'countryCode',       label: 'Country',   className: 'w-24', render: r => r.countryCode ?? '—' },
  { key: 'displayOrder',      label: 'Order',     className: 'w-16' },
  { key: 'isActive',          label: 'Status',    className: 'w-20',
    render: r => <Badge variant={r.isActive ? 'success' : 'default'}>{r.isActive ? 'Active' : 'Inactive'}</Badge> },
];

function dtoToForm(dto: CashDenominationDto): FormValues {
  return {
    currencyCode: dto.currencyCode,
    denominationValue: dto.denominationValue,
    denominationType: dto.denominationType,
    countryCode: dto.countryCode ?? null,
    displayOrder: dto.displayOrder,
    isActive: dto.isActive,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

export const CashDenominationPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const companyId = useSelector((s: RootState) => s.session.session?.companyId) ?? '';

  const canCreate = hasPermission(Permissions.CASH_DENOM_CREATE);
  const canEdit   = hasPermission(Permissions.CASH_DENOM_EDIT);
  const canDelete = hasPermission(Permissions.CASH_DENOM_DELETE);

  const screen = useMasterScreenState({ canEdit, canDelete });

  const { flashMsg, flash } = useFlashMessage();

  const [record,       setRecord]       = useState<CashDenominationDto | null>(null);
  const [activeTab,    setActiveTab]    = useState<Tab>('General');
  const [searchOpen,   setSearchOpen]   = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [lookupBusy,   setLookupBusy]   = useState(false);

  const [pendingSave,       setPendingSave]       = useState<FormValues | null>(null);
  const [showSaveConfirm,   setShowSaveConfirm]   = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const {
    register, handleSubmit, control, reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY_FORM });

  const resetToInitial = useCallback(() => {
    setRecord(null); reset(EMPTY_FORM);
    screen.setMode('initial'); setActiveTab('General'); setPendingSave(null);
  }, [reset, screen]);

  const loadRecord = useCallback((dto: CashDenominationDto) => {
    setRecord(dto); reset(dtoToForm(dto));
    screen.setMode('view'); setActiveTab('General');
  }, [reset, screen]);

  const handleEdit      = () => screen.setMode('edit');
  const handleRefresh   = () => resetToInitial();
  const handleSaveClick = () => { handleSubmit(v => { setPendingSave(v); setShowSaveConfirm(true); })(); };

  const executeSave = async () => {
    if (!pendingSave) return;
    setShowSaveConfirm(false); setSaving(true);
    try {
      const d = pendingSave;
      if (screen.isNew) {
        if (!canCreate) { flash('error', 'No permission to create.'); return; }
        await cashDenominationApi.create({
          companyId,
          currencyCode: d.currencyCode.trim().toUpperCase(),
          denominationValue: d.denominationValue,
          denominationType: d.denominationType,
          countryCode: d.countryCode?.trim() || null,
          displayOrder: d.displayOrder,
        });
        flash('success', 'Cash denomination created successfully.');
        resetToInitial();
      } else if (screen.isEdit && record) {
        await cashDenominationApi.update(record.id, {
          currencyCode: d.currencyCode.trim().toUpperCase(),
          denominationValue: d.denominationValue,
          denominationType: d.denominationType,
          countryCode: d.countryCode?.trim() || null,
          displayOrder: d.displayOrder,
          isActive: d.isActive,
        });
        flash('success', 'Cash denomination updated successfully.');
        resetToInitial();
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Save failed.';
      flash('error', msg);
    } finally { setSaving(false); setPendingSave(null); }
  };

  const handleDelete = () => setShowDeleteConfirm(true);
  const executeDelete = async () => {
    if (!record) return;
    setShowDeleteConfirm(false); setSaving(true);
    try {
      await cashDenominationApi.remove(record.id);
      flash('success', 'Cash denomination deleted.'); resetToInitial();
    } catch { flash('error', 'Delete failed.'); }
    finally { setSaving(false); }
  };

  const handleExit = () => navigate(AppRoutes.DASHBOARD);

  const handleNewRecord = () => {
    if (!canCreate) { flash('error', 'No permission to create.'); return; }
    reset(EMPTY_FORM); setRecord(null);
    screen.setMode('new'); setActiveTab('General');
  };

  const fetchItems = useCallback(async (search: string, page: number, pageSize: number) => {
    const res = await cashDenominationApi.getList({ companyId, search, pageNumber: page, pageSize });
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  }, [companyId]);

  const handleSearchSelect = (item: CashDenominationListDto) => {
    setSearchOpen(false); setLookupBusy(true);
    cashDenominationApi.getById(item.id)
      .then(res => { if (res.data.data) loadRecord(res.data.data); })
      .catch(() => flash('error', 'Failed to load record.'))
      .finally(() => setLookupBusy(false));
  };

  const fieldsDisabled = screen.isInitial || screen.isView;

  /** Binds a boolean field to the shared Toggle. */
  const ToggleField = ({ name, label, description }: { name: 'isActive'; label: string; description?: string }) => (
    <Controller name={name} control={control} render={({ field }) => (
      <Toggle
        label={label}
        description={description}
        checked={field.value as boolean}
        onChange={field.onChange}
        disabled={fieldsDisabled}
      />
    )} />
  );

  // ── Render ─────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full min-h-0">
      <MasterPageHeader icon={Coins} title="Cash Denomination Setup"
        subtitle="Configure currency denominations (notes and coins)" screen={screen} />

      <MasterToolbar
        {...screen.toolbar} saving={saving} loading={lookupBusy}
        onSave={handleSaveClick} onEdit={handleEdit}
        onDelete={handleDelete} onRefresh={handleRefresh} onExit={handleExit}
      />

      <MasterFlash message={flashMsg} />

      <MasterLookupBar
        label="Denomination"
        summary={record && (<>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {record.currencyCode} {record.denominationValue.toFixed(2)}
            </span>
            <Badge variant={record.denominationType === 'Note' ? 'info' : 'default'}>{record.denominationType}</Badge>
            {!record.isActive && (
              <span className="px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 text-[10px] font-bold">Inactive</span>
            )}
        </>)}
      >
        <Button
          variant="outline"
          size="sm"
          leftIcon={<Search size={13} />}
          loading={lookupBusy}
          onClick={() => setSearchOpen(true)}
          disabled={screen.isNew || screen.isEdit || saving}
        >
          Browse
        </Button>
        {screen.isInitial && canCreate && (
          <Button variant="outline" size="sm" leftIcon={<Plus size={13} />} onClick={handleNewRecord}>
            New
          </Button>
        )}
      </MasterLookupBar>

      <div className="flex-1 overflow-auto">
        <form onSubmit={e => e.preventDefault()} noValidate>
          <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

          <div className="p-5 space-y-5 max-w-2xl">

            {activeTab === 'General' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">Denomination Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Currency Code *"
                    {...register('currencyCode')}
                    error={errors.currencyCode?.message}
                    disabled={fieldsDisabled || screen.isEdit}
                    placeholder="e.g. LKR"
                    className="uppercase"
                  />
                  <Input
                    label="Denomination Value *"
                    type="number"
                    step="0.01"
                    {...register('denominationValue')}
                    error={errors.denominationValue?.message}
                    disabled={fieldsDisabled}
                    placeholder="e.g. 1000"
                  />
                  {/* Two mutually exclusive choices, so radios rather than a dropdown: both
                      options stay visible and it is one click, not two. The kit has no radio
                      group and one caller does not justify inventing one — Field supplies the
                      label and error so it still reads like every other field on the form. */}
                  <Field label="Denomination Type *" error={errors.denominationType?.message}>
                    <Controller name="denominationType" control={control} render={({ field }) => (
                      <div className="flex gap-4 h-8 items-center">
                        {(['Note', 'Coin'] as const).map(t => (
                          <label key={t} className={cn('flex items-center gap-2', fieldsDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer')}>
                            <input
                              type="radio"
                              value={t}
                              checked={field.value === t}
                              onChange={() => field.onChange(t)}
                              disabled={fieldsDisabled}
                              className="accent-brand-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">{t}</span>
                          </label>
                        ))}
                      </div>
                    )} />
                  </Field>
                  <Input
                    label="Country Code"
                    {...register('countryCode')}
                    disabled={fieldsDisabled}
                    placeholder="e.g. LK"
                  />
                  <Input
                    label="Display Order"
                    type="number"
                    {...register('displayOrder')}
                    error={errors.displayOrder?.message}
                    disabled={fieldsDisabled}
                    placeholder="0"
                  />
                  {(screen.isEdit || screen.isView) && (
                    <div className="sm:col-span-2">
                      <ToggleField name="isActive" label="Is Active" description="Inactive denominations are hidden from cash counting screens." />
                    </div>
                  )}
                </div>
              </Card>
            )}

            {activeTab === 'Audit' && <MasterAuditTab record={record} />}
          </div>
        </form>
      </div>

      <MasterSearchModal<CashDenominationListDto>
        isOpen={searchOpen} onClose={() => setSearchOpen(false)}
        title="Browse Cash Denominations" columns={SEARCH_COLUMNS}
        fetchData={fetchItems} onSelect={handleSearchSelect}
      />

      <ConfirmDialog isOpen={showSaveConfirm} variant="save"
        title={screen.isNew ? 'Save New Denomination' : 'Save Changes'}
        message={screen.isNew ? 'Save this new cash denomination?' : 'Save changes to this denomination?'}
        confirmLabel="Yes, Save" cancelLabel="No" loading={saving}
        onConfirm={executeSave} onCancel={() => { setShowSaveConfirm(false); setPendingSave(null); }}
      />

      <ConfirmDialog isOpen={showDeleteConfirm} variant="delete"
        title="Delete Denomination"
        message={<span>Delete this denomination?<br /><span className="text-xs text-gray-400">This action cannot be undone.</span></span>}
        confirmLabel="Yes, Delete" cancelLabel="No" loading={saving}
        onConfirm={executeDelete} onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
};
